import { useState, useEffect } from "react";

import dayjs from "dayjs";
import classNames from "clsx";

import Form from "react-bootstrap/Form";

import { useApi } from "@/hooks/useApi";
import { useSwal } from "@/hooks/useSwal";
import { useThemeClassNames } from "@/hooks/useThemeClassNames";

import { transformData } from "@/utils/transformData";
import { patientMap } from "@/utils/mappings/patient";

import { Race } from "@/enums/Race";

import { ActionButton } from "@/components/HospitalizationPermits/ActionButton";
import { ListOrFormControlUpdate } from "../ListOrFormControlUpdate";

type Props = {
	attendance: Attendance;
	setAttendance: (attendance: Attendance) => void;
};

export function FormPatient({ attendance, setAttendance }: Props) {
	const [isEditingPatient, setIsEditingPatient] = useState(false);
	const [loading, setLoading] = useState(true);
	const { border } = useThemeClassNames();
	const { Toast } = useSwal();
	const { api } = useApi();

	const { patient } = attendance;

	const birthdate = patient?.birthdate ? dayjs(patient.birthdate).format("DD/MM/YYYY") : "";

	function getSex(gender: string) {
		if (!gender) {
			return;
		}

		if (gender === "M") {
			return "MASCULINO";
		} else if (gender === "F") {
			return "FEMININO";
		}

		return "OUTROS";
	}

	async function handleUpdatePatient() {
		try {
			const formPatient = document.getElementById("form-patient") as HTMLFormElement;
			const formData = new FormData(formPatient);

			const requestData = Object.fromEntries(formData.entries());

			const { data } = await api.put(`/patients/${patient.id}`, requestData);

			const transformedPatient = transformData<Patient>(data, patientMap);

			setAttendance({
				...attendance,
				patient: transformedPatient,
			});

			setIsEditingPatient(false);

			Toast.fire({
				text: "Paciente editado com sucesso.",
				icon: "success",
			});
		} catch (error) {
			console.log(error);

			Toast.fire({
				text: "Não foi possível editar este paciente.",
				icon: "error",
			});
		}
	}

	const fieldsToVerify = [
		"name",
		"birthdate",
		"sex",
		"nationalHealthCard",
		"secondaryPhone",
		"nameMother",
		"phone",
		"identifier",
		"race",
		"zipCode",
		"neighborhood",
		"city",
		"address",
		"state",
		"number",
	];

	const hasSomeBlankField = Object.entries(patient || {}).some(
		([key, value]) => fieldsToVerify.includes(String(key)) && !value
	);

	useEffect(() => {
		if (Object.entries(patient || {}).length) {
			setLoading(false);
		}
	}, [patient]);

	return (
		<Form id="form-patient">
			<div className="flex-responsive d-flex p-4">
				<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
					<ListOrFormControlUpdate
						id="name"
						name="nome"
						label="Nome do Paciente:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.name}
					/>

					<ListOrFormControlUpdate
						id="birthdate"
						name="birthdate"
						type="date"
						label="Data de Nascimento:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={birthdate}
					/>

					<ListOrFormControlUpdate
						id="sex"
						name="sexo"
						label="Sexo:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={getSex(patient?.sex)}
					/>
				</div>

				<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
					<ListOrFormControlUpdate
						id="national-health-card"
						name="cns"
						label="CNS:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.nationalHealthCard}
					/>

					<ListOrFormControlUpdate
						id="mother-name"
						name="name_mother"
						label="Nome da Mãe:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.nameMother}
					/>

					<ListOrFormControlUpdate
						id="phone"
						name="celular"
						label="Contato¹:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.phone}
					/>
				</div>

				<div className="col-4 responsive-columns d-flex flex-column gap-2">
					<ListOrFormControlUpdate
						id="identifier"
						name="cpf"
						label="CPF:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.identifier}
					/>

					<ListOrFormControlUpdate
						id="race"
						name="raca_cor"
						isEditing={isEditingPatient}
						skeleton={loading}
						label="Raça / Cor:"
						data={Race[Number(patient?.race)]}
					/>

					<ListOrFormControlUpdate
						id="secondary-phone"
						name="celular_contato"
						label="Contato²:"
						isEditing={isEditingPatient}
						skeleton={loading}
						data={patient?.secondaryPhone}
					/>
				</div>
			</div>

			<div className={classNames("p-4 border-bottom-0 border-start-0 border-end-0", border)}>
				<div className="flex-responsive d-flex">
					<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
						<ListOrFormControlUpdate
							id="zip-code"
							name="cep"
							label="CEP:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.zipCode}
						/>

						<ListOrFormControlUpdate
							id="neighborhood"
							name="bairro"
							label="Bairro:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.neighborhood}
						/>
					</div>

					<div className="col-4 responsive-columns d-flex flex-column gap-2 pe-4">
						<ListOrFormControlUpdate
							id="city"
							name="cidade"
							type="select"
							label="Município:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.city}
						/>

						<ListOrFormControlUpdate
							id="address"
							name="logradouro"
							label="Endereço:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.address}
						/>
					</div>

					<div className="col-4 responsive-columns d-flex flex-column gap-2">
						<ListOrFormControlUpdate
							id="state"
							name="uf"
							type="select"
							label="Estado:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.state}
						/>

						<ListOrFormControlUpdate
							id="number"
							name="numero"
							label="N°:"
							isEditing={isEditingPatient}
							skeleton={loading}
							data={patient?.number}
						/>
					</div>
				</div>

				<div className="d-flex justify-content-end mt-2">
					{!loading && (
						<ActionButton
							isEditing={isEditingPatient}
							setIsEditing={setIsEditingPatient}
							data={patient}
							handleUpdateData={handleUpdatePatient}
							hasSomeBlankField={hasSomeBlankField}
						/>
					)}
				</div>
			</div>
		</Form>
	);
}
